import React from 'react';
import { styled } from '@stitches/react';
import AnimatedSection from '../../components/common/AnimatedSection';

// ===================== 页面整体布局和样式 =====================

// 帮助中心页面的最外层容器，设置整体宽度、内边距和字体颜色
const HelpPageContainer = styled('div', {
  padding: '4rem 2rem',
  maxWidth: '1200px',
  margin: '0 auto',
  color: 'var(--text-primary)',
  '@media (max-width: 768px)': {
    padding: '3rem 1rem',
  },
});

// 页面头部区域，居中显示
const HelpHeader = styled('div', {
  textAlign: 'center',
  marginBottom: '3rem',
});

// 页面主标题样式
const HelpTitle = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 700,
  marginBottom: '1.5rem',
});

// 页面描述文字样式
const HelpDescription = styled('p', {
  fontSize: '1.2rem',
  color: 'var(--text-secondary)',
  maxWidth: '800px',
  margin: '0 auto',
  lineHeight: 1.6,
});

// ===================== 新手指南区块样式 =====================

// 新手指南整体区块，卡片风格，左右布局
const StarterGuideSection = styled('div', {
  background: 'var(--card-bg-color)',
  borderRadius: 'var(--border-radius-lg)',
  padding: '3rem',
  marginBottom: '4rem',
  boxShadow: 'var(--shadow-md)',
  display: 'flex',
  justifyContent: 'space-between',
  '@media (max-width: 768px)': {
    padding: '2rem 1.5rem',
    flexDirection: 'column-reverse', // 小屏幕下纵向排列，插画在上方
  },
});

// 新手指南左侧内容区
const StarterGuideContent = styled('div', {
  textAlign: 'left',
  flex: '0 0 60%', // 占据60%宽度
  '@media (max-width: 768px)': {
    flex: '1',
    marginTop: '2rem', // 在移动端添加顶部间距，与上方插画分开
  },
});

// 新手指南右侧插画区
const StarterGuideIllustration = styled('div', {
  flex: '0 0 35%', // 占据35%宽度
  '@media (max-width: 768px)': {
    flex: '1',
    height: '200px', // 增加移动端下插画区高度
    marginTop: '0', // 移除顶部边距，因为它现在在顶部
  },
  display: 'flex',  // 使用flex居中插画
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative', // 添加相对定位，用于定位背景
});

// 不规则圆弧浅灰色背景
const IrregularBackground = styled('div', {
  position: 'absolute',
  width: '85%',
  height: '85%',
  background: 'var(--background-light,rgba(216, 216, 216, 0.98))',
  borderRadius: '60% 40% 50% 30% / 40% 50% 30% 60%', // 不规则圆弧形状
  opacity: 0.6,
  zIndex: 0,
  transform: 'rotate(-5deg)', // 稍微旋转背景
});

// 插画图片样式，保证自适应和圆角
const IllustrationImage = styled('img', {
  maxWidth: '70%',
  height: 'auto',
  borderRadius: 'var(--border-radius-md)',
  objectFit: 'contain',
  position: 'relative', // 确保图片在背景上方
  zIndex: 1,
});

// 新手指南标题
const StarterGuideTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 600,
  marginBottom: '1.5rem',
});

// 新手指南描述
const StarterGuideDescription = styled('p', {
  fontSize: '1.1rem',
  color: 'var(--text-secondary)',
  maxWidth: '700px',
  marginBottom: '2rem',
  lineHeight: 1.6,
});

// 新手指南按钮样式
const StarterGuideButton = styled('a', {
  display: 'inline-block',
  padding: '0.75rem 2rem',
  background: 'var(--button-primary)',
  color: 'var(--button-primary-text)',
  fontWeight: 600,
  borderRadius: 'var(--border-radius-md)',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'var(--button-primary-hover)',
    transform: 'translateY(-2px)',
  },
});

// ===================== 文章与分类区块样式 =====================

// 区块标题样式
const SectionTitle = styled('h2', {
  fontSize: '1.75rem',
  fontWeight: 600,
  marginBottom: '2rem',
  textAlign: 'center',
});

// 文章区块的行布局 (未使用)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArticlesSectionRow = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  gap: '2rem',
  marginBottom: '4rem',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
  },
});

// 文章分类区块 (未使用)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArticlesSection = styled('div', {
  flex: 1,
});

// 热门文章区块 (未使用)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const TopArticlesSection = styled('div', {
  flex: 1,
  background: 'var(--card-bg-color)',
  borderRadius: 'var(--border-radius-lg)',
  padding: '2rem',
  boxShadow: 'var(--shadow-md)',
});

// 单个分类区块 (未使用)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArticleCategory = styled('div', {
  marginBottom: '2rem',
});

// 分类标题 (未使用)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CategoryTitle = styled('h3', {
  fontSize: '1.2rem',
  fontWeight: 600,
  marginBottom: '1rem',
  padding: '0.5rem 0',
  borderBottom: '1px solid var(--border-color)',
});

// 文章列表样式 (未使用)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArticleList = styled('ul', {
  listStyle: 'none',
  padding: 0,
});

// 单个文章项样式 (未使用)
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArticleItem = styled('li', {
  padding: '0.5rem 0',
  '& a': {
    color: 'var(--secondary-color)',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    '&:hover': {
      color: 'var(--secondary-light)',
    },
    '&::before': {
      content: '""',
      width: '6px',
      height: '6px',
      backgroundColor: 'var(--secondary-color)',
      borderRadius: '50%',
      marginRight: '10px',
      display: 'inline-block',
    }
  }
});

// ===================== FAQ区块样式 =====================

const FaqSection = styled('div', {
  marginTop: '4rem',
});

const FaqHeader = styled('div', {
  textAlign: 'center',
  marginBottom: '2.5rem',
});

const FaqContainer = styled('div', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  gap: '1.5rem',
  maxWidth: '100%',
  margin: '0 auto',
});

const FaqItem = styled('div', {
  background: 'var(--card-bg-color)',
  borderRadius: 'var(--border-radius-md)',
  padding: '1.5rem',
  boxShadow: 'var(--shadow-sm)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: 'var(--shadow-md)',
  }
});

const FaqQuestion = styled('h3', {
  fontSize: '1.2rem',
  marginBottom: '1rem',
  fontWeight: 600,
  color: '#ffffff',
});

const FaqAnswer = styled('div', {
  fontSize: '1rem',
  lineHeight: 1.6,
  color: 'var(--text-secondary)',
  '& p': {
    marginBottom: '1rem',
  },
  '& p:last-child': {
    marginBottom: 0,
  },
  '& ul': {
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
  },
  '& table': {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '1rem',
  },
  '& th, & td': {
    border: '1px solid var(--border-color)',
    padding: '0.5rem',
    textAlign: 'left',
  },
  '& th': {
    backgroundColor: 'var(--background-color)',
  }
});

// ===================== 联系客服区块样式 =====================

const ContactSupportSection = styled('div', {
  background: 'var(--card-bg-color)',
  borderRadius: 'var(--border-radius-lg)',
  padding: '2.5rem',
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: '4rem',
  boxShadow: 'var(--shadow-md)',
  '@media (max-width: 768px)': {
    flexDirection: 'column',
    padding: '2rem 1.5rem',
  },
});

const ContactSupportContent = styled('div', {
  textAlign: 'left',
  flex: '0 0 60%',
  '@media (max-width: 768px)': {
    flex: '1',
  },
});

const ContactSupportIllustration = styled('div', {
  flex: '0 0 35%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  position: 'relative',
  '@media (max-width: 768px)': {
    flex: '1',
    height: '200px',
    marginTop: '2rem',
  },
});

const ContactTitle = styled('h3', {
  fontSize: '1.5rem',
  fontWeight: 600,
  marginBottom: '1rem',
});

const ContactDescription = styled('p', {
  fontSize: '1.1rem',
  color: 'var(--text-secondary)',
  maxWidth: '600px',
  marginBottom: '1.5rem',
  lineHeight: 1.6,
});

const ContactButton = styled('a', {
  display: 'inline-block',
  padding: '0.75rem 2rem',
  background: 'var(--button-primary)',
  color: 'var(--button-primary-text)',
  fontWeight: 600,
  borderRadius: 'var(--border-radius-md)',
  textDecoration: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'var(--button-primary-hover)',
    transform: 'translateY(-2px)',
  },
});

const ResponseTime = styled('p', {
  fontSize: '0.9rem',
  color: 'var(--text-secondary)',
  marginTop: '1rem',
});

// ===================== 组件主体 =====================
/**
 * 帮助中心页面组件
 * 展示常见问题、使用指南和支持渠道
 */
const HelpCenter: React.FC = () => {
  // const { t } = useTranslation(); // 暂时未使用
  
  // 帮助分类数据和热门文章数据已删除 - 当前未使用
  
  // 常见问题FAQ数据，每项包含问题和HTML格式答案
  const faqs = [
    {
      question: 'How does crypto mining work at BeMine?',
      answer: `
        <p>It couldn't be simpler! All you have to do is pay for the equipment and electricity. Our service administration and partners handle all other aspects of the process.</p>
        <p>Our partners are among the most prominent mining organizations in the Russian Federation, and the suppliers we collaborate with have been leaders in the industry for years.</p>
        <p>Once we receive your payment, your equipment will be accessible in your account. You'll then be responsible for installation and paying for electricity. Once set up, you'll continuously mine cryptocurrency, and the earnings will be displayed in your account. You can withdraw funds from your account to a bank card or bitcoin wallet.</p>
      `
    },
    {
      question: 'Why should I mine in 2025?',
      answer: `
        <p>The phrase "why mine in 2016, 2018, 2020, 2022, and now in 2025?" has become well-known in the community. The cryptocurrency industry is characterized by active market movements, requiring constant vigilance and understanding, particularly for inexperienced participants.</p>
        <p>It's crucial to remember that all investments, especially in such a volatile market, need continuous oversight. BeMine has been in development since 2017. Anticipating a significant correction in Bitcoin and major cryptocurrencies, we decided to wait out the downturn.</p>
        <p>In the second half of 2017, the price of one ASIC miner reached $5,200. It quickly became evident that at these prices, mining would soon become less viable. We waited and worked for an entire year until the cryptocurrency market finally stabilized.</p>
        <p>Today, cryptocurrency mining remains a highly profitable and relatively stable venture. This is why we are re-entering the market with renewed vigor and fresh ideas. Our response to the question is simple: mining is always profitable.</p>
      `
    },
    {
      question: 'What is "PRE-ORDER"?',
      answer: `
        <p>Pre-order is a unique opportunity to purchase the desired equipment at a reduced cost, significantly below market price under unique conditions.</p>
        <p>This is an incredible opportunity for those who want to save money, while still getting completely new equipment a few months after payment. After making a payment, the equipment order immediately goes into processing. Your equipment is reserved for you in line for arrival and installation in a specific data center.</p>
        <p>In addition, you also get a warranty on pre-ordered equipment for a period of 6 months from the supplier, which is an additional advantage of pre-ordering.</p>
        <p>Pre-ordering is a unique offer that allows you to worry about modern equipment in advance and allows you to start mining Cryptocurrency much earlier than the pre-order miner appears on the retail market. Typically, after the arrival of pre-orders, equipment appears in our retail within 23 months after the arrival of pre-orders, which statistically allows you to significantly accelerate the recoupment of funds.</p>
      `
    },
    {
      question: 'What is the service fee?',
      answer: `
        <p>The main fee for using BeMine services pays for the electricity consumed by your ASIC miners. The cost of electricity at the moment is $0.045 per kW/h.</p>
        <p>BeMine also charges commissions for the withdrawals of your funds designed to cover the costs of the bitcoin network and the service's expenses.</p>
      `
    },
    {
      question: 'What is the minimum withdrawal amount and why?',
      answer: `
        <p>At BeMine, the minimum withdrawal amount is set to ensure a smooth and efficient transaction process for all our users. Currently, the minimum withdrawal amount is 0.005 BTC. Here's why we have implemented this policy:</p>
        <ol>
          <li><strong>Transaction Costs:</strong> Every transaction incurs certain costs, including network fees and processing fees. By setting a minimum withdrawal amount, we can ensure that these costs are proportionately smaller compared to the withdrawal amount, making it more cost-effective for both our users and the platform.</li>
          <li><strong>Security and Fraud Prevention:</strong> Higher minimum withdrawal amounts help in reducing the risk of fraudulent activities. It acts as a deterrent for potential malicious actors who may attempt to exploit the system with numerous small withdrawals. This helps in maintaining a secure environment for all our users.</li>
          <li><strong>Operational Efficiency:</strong> Processing numerous small withdrawals can be operationally burdensome. By having a minimum withdrawal threshold, we can streamline our operations and focus on providing better services and support to our users.</li>
          <li><strong>User Benefit:</strong> Larger withdrawal amounts mean fewer transactions over time, which can be more convenient for users. This reduces the frequency of transaction fees impacting your earnings, allowing you to retain more of your mined cryptocurrency.</li>
        </ol>
        <p>We understand that our users might have different financial needs and expectations. Therefore, we are continuously monitoring and assessing our withdrawal policies to strike the right balance between operational efficiency, security, and user convenience. If you have any further questions or need assistance, please do not hesitate to contact our support team.</p>
      `
    }
  ];
  
  // ===================== 页面渲染 =====================
  return (
    <HelpPageContainer>
      {/* 页面头部动画区块 */}
      <AnimatedSection>
        <HelpHeader>
          <HelpTitle>BITENGINE Help Center</HelpTitle>
          <HelpDescription>
            Whether you're a novice or an experienced user of BITENGINE, find answers to your questions and get the support you need.
          </HelpDescription>
        </HelpHeader>
      </AnimatedSection>

      {/* 新手指南区块，含插画 */}
      <AnimatedSection delay={0.1}>
        <StarterGuideSection>
          <StarterGuideContent>
            <StarterGuideTitle>BITENGINE starter guide</StarterGuideTitle>
            <StarterGuideDescription>
              Whether you're a novice or an experienced user of BITENGINE, in starter guide you will familiarize yourself with the platform, starting from registration to withdrawing income.
            </StarterGuideDescription>
            <StarterGuideButton href="#">Read guide</StarterGuideButton>
          </StarterGuideContent>
          <StarterGuideIllustration>
            <IrregularBackground />
            <IllustrationImage 
              src="/images/help1.png" 
              alt="BITENGINE starter guide illustration" 
            />
          </StarterGuideIllustration>
        </StarterGuideSection>
      </AnimatedSection>
      
      {/* FAQ区块 */}
      <AnimatedSection delay={0.3}>
        <FaqSection>
          <FaqHeader>
            <SectionTitle>FAQ</SectionTitle>
          </FaqHeader>
          <FaqContainer>
            {faqs.map((faq, index) => (
              <FaqItem key={index}>
                <FaqQuestion>{faq.question}</FaqQuestion>
                {/* 使用dangerouslySetInnerHTML渲染HTML格式答案 */}
                <FaqAnswer dangerouslySetInnerHTML={{ __html: faq.answer }} />
              </FaqItem>
            ))}
          </FaqContainer>
        </FaqSection>
      </AnimatedSection>
      
      {/* 联系客服区块 */}
      <AnimatedSection delay={0.4}>
        <ContactSupportSection>
          <ContactSupportContent>
            <ContactTitle>Have a question?</ContactTitle>
            <ContactDescription>
              Technical support staff will respond to your questions and assist you in the referral program. Available every day via chat, email, and messengers.
            </ContactDescription>
            <ContactButton href="#">Write a request</ContactButton>
            <ResponseTime>Average response time - 24 hours</ResponseTime>
          </ContactSupportContent>
          <ContactSupportIllustration>
            <IllustrationImage 
              src="/images/help2.jpg" 
              alt="Technical support illustration" 
              style={{ 
                maxWidth: '80%', 
                transform: 'scale(0.8)' 
              }}
            />
          </ContactSupportIllustration>
        </ContactSupportSection>
      </AnimatedSection>
    </HelpPageContainer>
  );
};

export default HelpCenter; 